import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Billing v2 status for a team — the only place that knows about trials.
 *
 * The legacy subscription payload cannot answer "is this team on a trial":
 * BillingService.normalizeStripeStatus maps Stripe's 'trialing' to 'active',
 * so a trialing team is indistinguishable from a paying one by the time it
 * reaches the client. The v2 entitlement carries plan='TRIAL' with the trial
 * end as its window, so that is what we ask.
 *
 * Fails quiet: a status failure leaves `status` null and the caller falls back
 * to the legacy view rather than showing a billing error.
 */

const API_URL = process.env.REACT_APP_API_SERVER_URL || 'https://backend.trodo.ai';

function authHeaders() {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const BILLING_CHANGED_EVENT = 'billing-status-changed';

/** Tell every billing reader on the page to read again. */
export function announceBillingChange() {
  window.dispatchEvent(new Event(BILLING_CHANGED_EVENT));
}

export default function useBillingV2Status(teamId) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/billing/v2/status`, {
        params: { teamId },
        headers: authHeaders(),
      });
      setStatus(res.data);
      // Returned as well as stored so a caller can act on THIS read. The
      // upgrade flow polls until the webhook has landed, and React state is
      // not visible to it inside the same tick.
      return res.data;
    } catch {
      setStatus(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => { reload(); }, [reload]);
  // Anything that changes billing (the overage switch) says so, and every
  // reader reads it again.
  useEffect(() => {
    const onChange = () => { reload(); };
    window.addEventListener(BILLING_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(BILLING_CHANGED_EVENT, onChange);
  }, [reload]);

  return {
    status,
    loading,
    reload,
    isTrial: status?.plan === 'TRIAL',
    trialEndsAt: status?.trialEndsAt || null,
  };
}
